import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Autocomplete, type Option } from '@finn-auto/design-system/atoms/autocomplete';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { Tabs, TabsList, TabsTrigger } from '@finn-auto/design-system/atoms/tabs';
import { LockKeyhole, Users } from 'lucide-react';
import {
  fetchCompanySalaryBands,
  fetchDepartmentSalaryBands,
  fetchDepartmentMemberBands,
  fetchPersonalSalaryBands,
  fetchRecruiterSalaryBands,
  fetchSalaryContext,
  fetchTeamMemberBands,
  fetchTeamSalaryBands,
  type SalaryBand,
  type SalaryBandMember,
  type SalaryView,
} from '../services/salaryBandsApi';
import { RatingGrantModal } from './salaryBands/RatingGrantModal';
import { SalaryBandsSkeleton, SalaryBandsTable } from './salaryBands/SalaryBandsTable';

const TAB_LABELS: Record<SalaryView, string> = {
  personal: 'My salary bands',
  team: 'My team',
  department: 'My department',
  company: 'Company',
  recruiter: 'Recruiter view',
};

const euro = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

function ErrorNotice({ message = 'Salary bands could not be loaded.' }: { message?: string }) {
  return (
    <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-900" role="alert">
      {message} Please refresh the page or contact People Operations if the problem persists.
    </div>
  );
}

function EmptyNotice({ children }: { children: string }) {
  return <div className="border border-pearl bg-snow p-6 text-sm text-iron">{children}</div>;
}

function MemberBandSummary({ member }: { member: SalaryBandMember }) {
  if (!member.hasSalaryBand) {
    return <EmptyNotice>No salary band is assigned to this team member in Personio.</EmptyNotice>;
  }
  return (
    <section className="grid gap-px border border-pearl bg-pearl sm:grid-cols-4">
      {[
        ['Current band min', member.min],
        ['Current band average', member.average],
        ['Current band max', member.max],
        ['Initial grant value', member.grantValueVsop],
      ].map(([label, value]) => (
        <div key={String(label)} className="bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-iron">{label}</p>
          <p className="mt-1 text-lg font-semibold tabular-nums text-black">
            {euro.format(Number(value))}
          </p>
        </div>
      ))}
    </section>
  );
}

function PersonalPanel({ onRating }: { onRating: (band: SalaryBand) => void }) {
  const query = useQuery({ queryKey: ['salary-bands', 'personal'], queryFn: fetchPersonalSalaryBands });
  if (query.isPending) return <SalaryBandsSkeleton />;
  if (query.isError) return <ErrorNotice />;
  if (query.data.remoteOutsideGermany) {
    return <EmptyNotice>Salary bands are currently available for Germany-based roles only.</EmptyNotice>;
  }
  // One column set and fixed widths for all three, so the stacked tables line up.
  const { current, next, careerPath } = query.data;
  const alignWith = [...current, ...next, ...careerPath];
  return (
    <div className="space-y-8">
      <SalaryBandsTable bands={current} title="Current level" onRating={onRating} alignWith={alignWith} />
      <SalaryBandsTable bands={next} title="Next level" onRating={onRating} alignWith={alignWith} />
      <SalaryBandsTable
        bands={careerPath}
        title="Career path"
        onRating={onRating}
        alignWith={alignWith}
        highlight={{ identifiers: current.map((band) => band.identifier), label: 'Your band' }}
      />
    </div>
  );
}

function TeamPanel({ onRating }: { onRating: (band: SalaryBand) => void }) {
  const teamQuery = useQuery({ queryKey: ['salary-bands', 'team'], queryFn: fetchTeamSalaryBands });
  const [selectedId, setSelectedId] = useState('');
  const effectiveSelectedId = selectedId || teamQuery.data?.reports[0]?.id || '';
  const selectedReport = teamQuery.data?.reports.find((report) => report.id === effectiveSelectedId);
  const bandsQuery = useQuery({
    queryKey: ['salary-bands', 'team-member', effectiveSelectedId],
    queryFn: () => fetchTeamMemberBands(effectiveSelectedId),
    enabled: Boolean(effectiveSelectedId && selectedReport?.hasSalaryBand),
  });
  if (teamQuery.isPending) return <SalaryBandsSkeleton />;
  if (teamQuery.isError) return <ErrorNotice />;
  if (teamQuery.data.reports.length === 0) return <EmptyNotice>No direct reports were found.</EmptyNotice>;

  return (
    <div className="space-y-6">
      <section className="border border-pearl bg-snow p-4 sm:p-6">
        <div className="max-w-md">
          <h2 className="text-sm font-semibold text-black">Team member</h2>
          <p className="mt-1 text-xs text-iron">
            Select a direct report to view their current band and career path.
          </p>
          <Select value={effectiveSelectedId} onValueChange={setSelectedId}>
            <SelectTrigger
              aria-label="Team member"
              className="mt-3 min-h-14 w-full border-pearl bg-white px-4 text-left"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {teamQuery.data.reports.map((report) => (
                <SelectItem key={report.id} value={report.id}>
                  {report.fullName} · {report.identifier || `Level ${report.level}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      <div className="space-y-6">
        {selectedReport ? <MemberBandSummary member={selectedReport} /> : null}
        {bandsQuery.isPending ? <SalaryBandsSkeleton /> : null}
        {bandsQuery.isError ? <ErrorNotice /> : null}
        {bandsQuery.data && selectedReport?.hasSalaryBand ? (
          <SalaryBandsTable
            bands={bandsQuery.data.bands}
            title={`${bandsQuery.data.employee.fullName} · career path`}
            onRating={onRating}
            highlight={{ identifiers: [selectedReport.identifier], label: 'Current band' }}
            showThirds
            searchable
          />
        ) : null}
      </div>
    </div>
  );
}

function DepartmentPanel({ onRating }: { onRating: (band: SalaryBand) => void }) {
  const query = useQuery({
    queryKey: ['salary-bands', 'department'],
    queryFn: fetchDepartmentSalaryBands,
  });
  const [selectedId, setSelectedId] = useState('');
  const members = query.data?.members;
  const selectedMember = members?.find((member) => member.id === selectedId);
  const options = useMemo<Option[]>(
    () =>
      (members || []).map((member) => ({
        value: member.id,
        label: `${member.fullName} · ${member.identifier}`,
      })),
    [members],
  );
  const selectedOption = options.find((option) => option.value === selectedId);
  const memberBandsQuery = useQuery({
    queryKey: ['salary-bands', 'department-member', selectedId],
    queryFn: () => fetchDepartmentMemberBands(selectedId),
    enabled: Boolean(selectedId),
  });
  if (query.isPending) return <SalaryBandsSkeleton />;
  if (query.isError) return <ErrorNotice />;
  return (
    <div className="space-y-6">
      <section className="border border-pearl bg-snow p-4 sm:p-6">
        <div className="max-w-md">
          <h2 className="text-sm font-semibold text-black">Department member</h2>
          <p className="mt-1 text-xs text-iron">
            Search for a colleague to view their current band and career path. Leave empty to view all department bands.
          </p>
          <Autocomplete
            aria-label="Department member"
            className="mt-3"
            options={options}
            value={selectedOption}
            onValueChange={(option) => setSelectedId(option?.value || '')}
            placeholder="Search by name or band identifier"
            shouldFilter
            openOnFocus
          />
        </div>
      </section>

      {selectedMember ? <MemberBandSummary member={selectedMember} /> : null}
      {selectedId && memberBandsQuery.isPending ? <SalaryBandsSkeleton /> : null}
      {memberBandsQuery.isError ? <ErrorNotice /> : null}
      {selectedMember && memberBandsQuery.data ? (
        <SalaryBandsTable
          bands={memberBandsQuery.data.bands}
          title={`${selectedMember.fullName} · career path`}
          highlight={{ identifiers: [selectedMember.identifier], label: 'Current band' }}
          showThirds
          onRating={onRating}
          searchable
        />
      ) : !selectedId ? (
        <SalaryBandsTable
          bands={query.data.bands}
          title={`${query.data.scope} · levels below ${query.data.levelExclusive}`}
          showThirds
          onRating={onRating}
          searchable
        />
      ) : null}
    </div>
  );
}

function CompanyPanel({ onRating }: { onRating: (band: SalaryBand) => void }) {
  const query = useQuery({ queryKey: ['salary-bands', 'company'], queryFn: fetchCompanySalaryBands });
  if (query.isPending) return <SalaryBandsSkeleton />;
  if (query.isError) return <ErrorNotice />;
  return (
    <SalaryBandsTable
      bands={query.data.bands}
      title="All company salary bands"
      showThirds
      showRating={query.data.canViewCompanyRating}
      onRating={onRating}
      searchable
    />
  );
}

function RecruiterPanel({ onRating }: { onRating: (band: SalaryBand) => void }) {
  const query = useQuery({
    queryKey: ['salary-bands', 'recruiter'],
    queryFn: fetchRecruiterSalaryBands,
  });
  if (query.isPending) return <SalaryBandsSkeleton />;
  if (query.isError) return <ErrorNotice />;
  return (
    <SalaryBandsTable
      bands={query.data.bands}
      title="Recruiting salary bands"
      showThirds
      allowDownload
      onRating={onRating}
      searchable
    />
  );
}

export default function SalaryBandsView() {
  const queryClient = useQueryClient();
  const contextQuery = useQuery({
    queryKey: ['salary-bands', 'context'],
    queryFn: fetchSalaryContext,
    staleTime: 5 * 60 * 1000,
  });
  const [activeView, setActiveView] = useState<SalaryView>('personal');
  const [ratingBand, setRatingBand] = useState<SalaryBand | null>(null);
  const allowedViews = useMemo(
    () =>
      contextQuery.data
        ? (Object.keys(TAB_LABELS) as SalaryView[]).filter((view) => contextQuery.data.views[view])
        : [],
    [contextQuery.data]
  );

  const effectiveView = allowedViews.includes(activeView) ? activeView : allowedViews[0];

  if (contextQuery.isPending) {
    return <div className="p-6 lg:p-10"><SalaryBandsSkeleton /></div>;
  }
  if (contextQuery.isError) {
    return <div className="p-6 lg:p-10"><ErrorNotice message="Your salary-band access could not be verified." /></div>;
  }
  if (!contextQuery.data.employee || allowedViews.length === 0) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-md border border-pearl bg-white p-8 text-center">
          <LockKeyhole className="mx-auto mb-4 h-9 w-9 text-iron" />
          <h1 className="text-xl font-semibold">Salary bands unavailable</h1>
          <p className="mt-2 text-sm text-iron">We could not match your account to an active employee record.</p>
        </div>
      </div>
    );
  }

  return (
    <main className="mx-auto w-full max-w-[96rem] space-y-6 p-4 sm:p-6 lg:p-10">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-iron"><Users className="h-4 w-4" />People</div>
          <h1 className="text-3xl font-semibold tracking-tight text-black">Salary bands</h1>
          <p className="mt-2 max-w-2xl text-sm text-iron">
            Compensation ranges are confidential. Your available views are determined server-side from your employee record and Google Groups.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['salary-bands'] })}
        >
          Refresh data
        </Button>
      </header>

      <Tabs value={effectiveView} onValueChange={(value) => setActiveView(value as SalaryView)}>
        <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto border-b border-pearl bg-transparent p-0">
          {allowedViews.map((view) => (
            <TabsTrigger key={view} value={view} className="whitespace-nowrap rounded-none px-4 py-3">
              {TAB_LABELS[view]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {effectiveView === 'personal' ? <PersonalPanel onRating={setRatingBand} /> : null}
      {effectiveView === 'team' ? <TeamPanel onRating={setRatingBand} /> : null}
      {effectiveView === 'department' ? <DepartmentPanel onRating={setRatingBand} /> : null}
      {effectiveView === 'company' ? <CompanyPanel onRating={setRatingBand} /> : null}
      {effectiveView === 'recruiter' ? <RecruiterPanel onRating={setRatingBand} /> : null}
      {ratingBand ? <RatingGrantModal band={ratingBand} onClose={() => setRatingBand(null)} /> : null}
    </main>
  );
}
